import { html, type TemplateResult } from 'lit';

/**
 * Reusable skeleton loaders. Lit-template helpers (no custom element overhead).
 *
 * Usage:
 *   ${skeletonLine({ width: 'w-32' })}
 *   ${skeletonRow()}
 *   ${skeletonCard()}
 *   ${skeletonStatGrid(5)}
 */

export function skeletonLine(opts: { width?: string; height?: string; className?: string } = {}): TemplateResult {
  const { width = 'w-full', height = 'h-4', className = '' } = opts;
  return html`<div class="skeleton ${width} ${height} ${className}"></div>`;
}

export function skeletonCard(): TemplateResult {
  return html`
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body gap-3">
        ${skeletonLine({ width: 'w-1/2', height: 'h-5' })}
        ${skeletonLine({ width: 'w-3/4' })}
        ${skeletonLine({ width: 'w-2/3' })}
      </div>
    </div>
  `;
}

export function skeletonRow(): TemplateResult {
  return html`
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body flex-row items-center gap-4 py-4">
        <div class="flex flex-1 flex-col gap-2">
          ${skeletonLine({ width: 'w-48', height: 'h-5' })}
          ${skeletonLine({ width: 'w-32', height: 'h-3' })}
        </div>
        <div class="flex gap-6">
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
          ${skeletonLine({ width: 'w-10', height: 'h-8' })}
        </div>
      </div>
    </div>
  `;
}

export function skeletonRows(count = 3): TemplateResult {
  return html`<div class="flex flex-col gap-3">${Array.from({ length: count }, () => skeletonRow())}</div>`;
}

export function skeletonStatCards(count = 5): TemplateResult {
  return html`
    <div class="grid grid-cols-2 gap-4 sm:grid-cols-${count}">
      ${Array.from(
        { length: count },
        () => html`
          <div class="card bg-base-200 shadow-sm">
            <div class="card-body items-center gap-2 text-center">
              ${skeletonLine({ width: 'w-12', height: 'h-8' })}
              ${skeletonLine({ width: 'w-20', height: 'h-3' })}
            </div>
          </div>
        `,
      )}
    </div>
  `;
}
